import debounce from 'lodash-es/debounce';
import { type MutableRefObject, useEffect, useRef } from 'react';

import { globalHeaderHeight } from '../GlobalHeader';

type UseWheelScrollAnimationProps = {
  childCount: number;
  elementRefs: MutableRefObject<Array<HTMLElement | null>>;
  isEnabled: boolean;
};

export const useWheelScrollAnimation = ({
  childCount,
  elementRefs,
  isEnabled,
}: UseWheelScrollAnimationProps) => {
  const visibleCardIndices = useRef(new Set<number>());
  const ticking = useRef(false);

  useEffect(() => {
    const cardElements = elementRefs.current.slice(0, childCount);

    if (!isEnabled) {
      for (const element of cardElements) {
        if (element) {
          element.style.transform = 'scale(1)';
        }
      }

      return;
    }

    const animateScales = () => {
      // + globalHeaderHeight to consider the "center" of the screen without the header
      const viewportCenterY = window.innerHeight / 2 + globalHeaderHeight;

      for (const cardIndex of visibleCardIndices.current) {
        const cardEl = cardElements[cardIndex];
        if (!cardEl) continue;

        const rect = cardEl.getBoundingClientRect();
        const cardCenterY = rect.top + rect.height / 2;
        const distance = Math.abs(viewportCenterY - cardCenterY);
        const deadZone = 30; // Period, in px, where scaling does not happen.
        const adjustedDistance = Math.max(0, distance - deadZone);
        const progress = Math.min(1, adjustedDistance / viewportCenterY);
        const easedProgress = progress * progress; // smooth the transition
        const scale = 1 - easedProgress * 0.3;

        cardEl.style.transform = `scale(${scale})`;
      }
    };

    const onScroll = () => {
      if (!ticking.current) {
        window.requestAnimationFrame(() => {
          animateScales();
          ticking.current = false;
        });
        ticking.current = true;
      }
    };

    const observerCallback = (entries: IntersectionObserverEntry[]) => {
      for (const entry of entries) {
        const targetEl = entry.target as HTMLDivElement;
        const targetIndex = cardElements.indexOf(targetEl);

        if (targetIndex === -1) return;

        if (entry.isIntersecting) {
          visibleCardIndices.current.add(targetIndex);
        } else {
          visibleCardIndices.current.delete(targetIndex);
        }
      }
    };

    const observer = new IntersectionObserver(observerCallback, {
      rootMargin: '20px',
      threshold: 0,
    });

    for (const element of cardElements) {
      if (element) {
        observer.observe(element);
      }
    }

    const debouncedAnimateScales = debounce(animateScales, 200, { trailing: true });

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', debouncedAnimateScales);
    animateScales();

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', debouncedAnimateScales);
      observer.disconnect();
      visibleCardIndices.current.clear();
    };
  }, [elementRefs, childCount, isEnabled]);
};
